import * as React from 'react';
import ReactDOM from 'react-dom';

import { Bone } from '@lightspeed/flame/Bone';
import { FlameTheme } from '@lightspeed/flame/Core';

import { createReactRenderer } from 'helpers/react-renderer';

import { addAnalyticsColumnHeaders } from './AnalyticsColumnHeaders';
import { CollapseSwitch } from './CollapseSwitch';
import { ErrorCell } from './ErrorCell';
import { PromoCell } from './PromoCell';
import { getPOItemsAnalytics } from './PurchaseItemsAnalyticsAPI';
import { PurchaseRow, ReorderData, SuggestedNeedItem } from './types';

// Calculate the suggested need and display the analytics data
function showData(reorderData: ReorderData, purchaseData: PurchaseRow[]) {
  const analyticsItems = reorderData.data || [];
  const suggestedNeeds: SuggestedNeedItem[] = [];
  analyticsItems.forEach((analyticsItem) => {
    const row = document.querySelector(`tr.item_id_${analyticsItem.item_id}`);
    const purchaseItem = purchaseData.find(
      (purchaseItem) => purchaseItem.item_id === analyticsItem.item_id
    );
    if (!row || !purchaseItem) return;

    const setCell = (selector: string, value: number) => {
      const cell = row.querySelector(`td.analytics_${selector}`);
      if (cell) ReactDOM.render(<>{value}</>, cell);
    };

    const suggestedNeed = Math.max(0, analyticsItem.dynamic_reorder_level - purchaseItem.total_qoh);

    suggestedNeeds.push({
      orderLineID: purchaseItem.purchase_item_id,
      quantity: suggestedNeed,
    });

    const truncatedSales =
      Math.round((analyticsItem.average_daily_sales + Number.EPSILON) * 100) / 100;

    setCell('sales', truncatedSales);
    setCell('period', analyticsItem.forecast_period);
    setCell('level', analyticsItem.dynamic_reorder_level);
    setCell('need', suggestedNeed);
  });
}

// Show loading bones for each cell
function showLoading() {
  const cells = document.querySelectorAll('td.analytics');

  cells.forEach((cell) => {
    ReactDOM.render(
      <FlameTheme>
        <Bone />
      </FlameTheme>,
      cell
    );
  });
}

type ConfigureCallback = (analyticsTable: Element, rootCell: HTMLTableCellElement) => void;

function configureAnalyticsCells(showCells: boolean, callback: ConfigureCallback) {
  const analyticsTable = document.querySelector('table.analytics');
  const analyticsCells = document.querySelectorAll('td.analytics');
  const rootCell = document.querySelector('td.analytics_root') as HTMLTableCellElement;

  if (!analyticsTable || !rootCell) return;

  // Remove hidden class when showing, add it when hiding
  if (showCells) {
    analyticsCells.forEach((cell) => {
      if (cell !== rootCell) cell.classList.remove('hidden');
    });
  } else {
    analyticsCells.forEach((cell) => {
      if (cell !== rootCell) cell.classList.add('hidden');
    });
  }

  callback(analyticsTable, rootCell);
}

// Collapse analytics columns and show promo overlay
function showPromo(rowCount: number) {
  configureAnalyticsCells(false, (analyticsTable, rootCell) => {
    analyticsTable.classList.add('analytics_collapsed');

    rootCell.rowSpan = rowCount;
    rootCell.colSpan = 1;
    rootCell.classList.add('analytics_left', 'analytics_bottom', 'analytics_promo');

    const renderer = createReactRenderer({
      component: PromoCell,
      selector: 'td.analytics_root',
      props: { rowCount },
    });

    renderer.mount();
  });
}

// Show error overlay
function showError(shopID: number, rows: PurchaseRow[]) {
  configureAnalyticsCells(false, (analyticsTable, rootCell) => {
    analyticsTable.classList.add('analytics_error');
    const colSpan = analyticsTable.classList.contains('analytics_collapsed') ? 1 : 3;

    rootCell.rowSpan = rows.length;
    rootCell.colSpan = colSpan;
    rootCell.classList.add('analytics_left', 'analytics_bottom');

    ReactDOM.render(<ErrorCell onReload={() => loadData(shopID, rows)} />, rootCell);
  });
}

// Reset column states, show loading, and load data
function loadData(shopID: number, rows: PurchaseRow[]) {
  configureAnalyticsCells(true, (analyticsTable, rootCell) => {
    analyticsTable.classList.remove('analytics_error');

    rootCell.rowSpan = 1;
    rootCell.colSpan = 1;
    rootCell.classList.remove('analytics_left', 'analytics_bottom');
  });

  showLoading();

  const ids = rows.map((row) => row.item_id);
  getPOItemsAnalytics(
    shopID,
    ids,
    (analytics) => showData(analytics, rows),
    () => showError(shopID, rows)
  );
}

type Props = {
  shopID: number;
  rows: PurchaseRow[];
  hasAnalytics: boolean;
  isCollapsed?: boolean;
};

const AnalyticsLoader: React.FC<Props> = ({ shopID, rows, hasAnalytics, isCollapsed }) => {
  const [lastUpdate, setLastUpdate] = React.useState(0);

  React.useEffect(() => {
    const pannel = window.getListingPannel('purchase_listings_items_view');
    if (pannel && pannel.listing) {
      pannel.listing.draw_all = true;
    }

    addAnalyticsColumnHeaders(hasAnalytics);

    if (!rows?.length) {
      return;
    }

    if (!hasAnalytics) {
      showPromo(rows.length);
      return;
    }

    if (!isCollapsed) {
      loadData(shopID, rows);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasAnalytics || !rows?.length) return <></>;

  return (
    <CollapseSwitch
      isCollapsedPref={isCollapsed}
      onShow={() => {
        if (lastUpdate > Date.now() - 5000) return;
        loadData(shopID, rows);
        setLastUpdate(Date.now());
      }}
    />
  );
};

const RenderedComponent = createReactRenderer({
  component: AnalyticsLoader,
  selector: '[data-react-root="AnalyticsLoader"]',
});

export default AnalyticsLoader;

history.onChangeState(
  [{ name: ['purchase.listings.items'], form_name: 'view', id: undefined }],
  () => RenderedComponent.mount(),
  () => RenderedComponent.unmount()
);
